import cn from "classnames"
import { Field, FieldProps } from "formik"
import React, { useEffect, useRef, useState } from "react"
/* eslint-disable @typescript-eslint/ban-ts-ignore */
// @ts-ignore
import Files from "react-files"
import { useTranslation } from "react-i18next"

import { getRequiredMessage } from "~/components/Form/helpers"

import { Raw } from "../raw"
import { useThemeContext } from "../Theme/Theme"
import styles from "./index.module.css"

type FileType = {
  id: string
  name: string
}

type FilesType = FileType & {
  removeFile: (file: FileType) => void
  removeFiles: () => void
}

type ErrorType = {
  code: 1 | 2 | 3 | 4
  message: string
}

type AttachFileProps = {
  onChange: (files: any) => void
  name: string
  value: any
  error?: boolean
  errorMessage?: string
} & AttachFileFieldProps

export const AttachFile: React.FC<AttachFileProps> = ({
  className,
  name,
  onChange,
  error,
  errorMessage,
  value,
  onlyPdf,
  isRequired,
}) => {
  const { t } = useTranslation()
  const { theme } = useThemeContext()
  const [uploadedFiles, setFiles] = useState<FilesType[]>([])
  const [errorMessages, setErrors] = useState<string[]>([])
  const inputEl = useRef<FilesType>(null)
  const onFilesChange = (files: FilesType[]): void => {
    onChange(files)
    setFiles(files)
  }

  const onFilesError = (error: ErrorType, file: FileType): void => {
    if (error.code === 2) {
      setErrors((prevState) => [
        ...prevState,
        `${file.name} ${t("popup.Default.Form.AttachFile.Errors.Size")}`,
      ])
    }
    // console.log("error code " + error.code + ": " + error.message)
  }

  const filesRemoveOne = (file: FileType): void => {
    setErrors([])
    inputEl.current?.removeFile(file)
  }

  const ResetErrors = (): void => {
    setErrors([])
  }

  useEffect(() => {
    if ((!value || value.length === 0) && uploadedFiles.length > 0) {
      inputEl.current?.removeFiles()
    }
  }, [value, uploadedFiles.length])

  return (
    <div className={cn(className, styles.Wrapper)}>
      <div className={styles.AddButtonWrapper}>
        <Files
          ref={inputEl}
          accepts={
            !onlyPdf
              ? [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".jpg", ".jpeg", ".png"]
              : [".pdf"]
          }
          className={cn(styles.AddButton, {
            [styles._error]: error,
          })}
          maxFileSize={8388608}
          multiple={false}
          name={name}
          clickable
          onChange={onFilesChange}
          onError={onFilesError}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: isRequired
                ? `${t("popup.Default.Form.AttachFile.AddButton")} *`
                : t("popup.Default.Form.AttachFile.AddButton"),
            }}
            onClick={ResetErrors}
          />
        </Files>

        <div
          className={styles.Notice}
          dangerouslySetInnerHTML={{
            __html: !onlyPdf
              ? t("popup.Default.Form.AttachFile.Notice")
              : t("popup.Default.Form.AttachFile.Notice.Pdf"),
          }}
        />
      </div>

      {errorMessage && <div className={styles.ErrorMessage}>{errorMessage}</div>}

      {uploadedFiles.length > 0 && (
        <div className={styles.Files}>
          {uploadedFiles.map((file) => (
            <div
              key={file.id}
              className={cn({
                [styles.File]: theme !== "light-blue",
                [styles.onLightBlue]: theme === "light-blue",
              })}
            >
              <Raw html={file.name} />
              <div
                className={styles.DeleteFile}
                id={file.id}
                onClick={() => filesRemoveOne(file)}
              />
            </div>
          ))}
        </div>
      )}

      {errorMessages.length !== 0 && (
        <div className={styles.Errors}>
          {errorMessages.map((message, i) => (
            <div key={i} className={styles.Error}>
              {message}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

type AttachFileFieldProps = {
  className?: string
  name?: string
  onlyPdf?: boolean
  isRequired?: boolean
}

export const AttachFileField: React.FC<AttachFileFieldProps> = ({
  className,
  name = "files",
  onlyPdf,
  isRequired,
}) => {
  const { t } = useTranslation()

  return (
    <Field name={name}>
      {({ field, form: { setFieldValue, setFieldTouched }, meta }: FieldProps) => {
        const onChange = (files: any): void => {
          setFieldValue(name, files)
          setFieldTouched(name, true, false)
        }

        return (
          <AttachFile
            className={className}
            error={!!meta.error && meta.touched}
            errorMessage={getRequiredMessage(t, meta, isRequired)}
            {...field}
            isRequired={isRequired}
            name={name}
            onlyPdf={onlyPdf}
            onChange={onChange}
          />
        )
      }}
    </Field>
  )
}
